import { useState, useRef, useCallback, useEffect } from 'react';
import { uploadFile, api } from '../api/client';
import { getCachedMediaUrl, cacheMediaUrl, cacheMediaUrls } from '../api/mediaCache';

interface ImageUploaderProps {
  siteId: string;
  value?: string; // Current image S3 key
  onChange: (key: string) => void;
  label?: string;
}

export default function ImageUploader({
  siteId,
  value,
  onChange,
  label,
}: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Resolve the display URL for the current value (S3 key)
  useEffect(() => {
    if (!value) {
      setDisplayUrl(null);
      return;
    }

    // Check if it's already a full URL (e.g. https://...)
    if (value.startsWith('http://') || value.startsWith('https://')) {
      setDisplayUrl(value);
      return;
    }

    // Check the cache first
    const cached = getCachedMediaUrl(value);
    if (cached) {
      setDisplayUrl(cached);
      return;
    }

    // If not cached, fetch media list to populate cache
    let cancelled = false;
    api.listMedia(siteId).then((items) => {
      if (cancelled) return;
      cacheMediaUrls(items.map((item) => ({ key: item.key, url: item.url })));
      const url = getCachedMediaUrl(value);
      setDisplayUrl(url);
    }).catch(() => {
      // Silently fail — image just won't show
    });

    return () => { cancelled = true; };
  }, [value, siteId]);

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      setUploading(true);
      setProgress(0);
      try {
        const result = await uploadFile(siteId, file, setProgress);
        // Cache the presigned URL from the upload response
        if (result.url) {
          cacheMediaUrl(result.key, result.url);
          setDisplayUrl(result.url);
        }
        onChange(result.key);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [siteId, onChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div className="image-uploader">
      {label && <label className="field-label">{label}</label>}
      <div
        className={`upload-zone ${dragOver ? 'drag-over' : ''} ${value ? 'has-image' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? (
          <div className="upload-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span>{progress}%</span>
          </div>
        ) : value ? (
          <div className="upload-preview">
            {displayUrl ? (
              <img
                src={displayUrl}
                alt="Upload preview"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="media-placeholder"><svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>
            )}
            <div className="upload-overlay">
              <span>Click or drop to replace</span>
            </div>
            <button
              type="button"
              className="upload-remove-btn"
              title="Remove image"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="upload-placeholder">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17,8 12,3 7,8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Drag & drop or click to upload</span>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      {value && (
        <div className="upload-info">
          <span className="upload-key" title={value}>
            {value.split('/').pop()}
          </span>
          <button
            type="button"
            className="btn-text btn-danger-text"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
